import React from 'react';
import { Alert, Checkbox, Content, Radio, Spinner } from '@patternfly/react-core';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { formatPrice } from '../../../utils/money';
import type { PaymentMethod } from '../../../types/models/PaymentMethod';
import type { PaymentSource } from '../../../types/models/Payment';
import type { Wallet } from '../../../types/models/Wallet';
import { balanceCovers, describeCard, formatCardExpiry, isUsableCard } from './paymentDisplay';

export interface PaymentSourceChooserProps {
  /** Unique prefix for the radio ids/name — several choosers may exist on one page. */
  idPrefix: string;
  value: PaymentSource;
  onChange: (source: PaymentSource) => void;
  saveCard: boolean;
  onSaveCardChange: (checked: boolean) => void;
  /**
   * The amount the wallet must cover (2-decimal string), or `null` when not known yet (the wallet
   * option is then disabled). Display gating only — the backend re-checks the balance.
   */
  amountDue: string | null;
  /** From `useWallet()` — the caller owns the fetch so it can defer it (modal open / step entry). */
  wallet: Wallet | null;
  defaultPaymentMethod: PaymentMethod | null;
  walletLoading: boolean;
  walletError: string | null;
  /** Hide the wallet option entirely (a top-up cannot be paid from the wallet). Default `true`. */
  allowWallet?: boolean;
  /** Disable every control (a submission is in flight). */
  isDisabled?: boolean;
}

/**
 * The three payment sources every "pay" surface offers — prepaid balance, saved card, hosted
 * checkout — as a radio group, with the "save this card" checkbox nested under checkout. Shared by
 * the Pay-invoice modal, the Top-up modal (with `allowWallet={false}`) and the domain-order recap
 * steps, so the gating rules live in exactly one place:
 *
 * - `wallet` is selectable only when the balance covers `amountDue`.
 * - `card` is selectable only when a usable default card exists (`valid`, not expired).
 * - Under an **impersonation session** the backend refuses `wallet`/`card` and forces `save_card`
 *   off (a staff member never moves a customer's money), so those controls are disabled here and
 *   the checkout stays the only choice.
 *
 * When the current `value` becomes unselectable (e.g. the wallet loaded and can't cover the amount)
 * the chooser snaps back to `checkout` so an impossible choice is never submitted.
 */
const PaymentSourceChooser: React.FunctionComponent<PaymentSourceChooserProps> = ({
  idPrefix,
  value,
  onChange,
  saveCard,
  onSaveCardChange,
  amountDue,
  wallet,
  defaultPaymentMethod,
  walletLoading,
  walletError,
  allowWallet = true,
  isDisabled = false,
}) => {
  const { isImpersonating } = useImpersonation();

  const walletCovers = wallet !== null && amountDue !== null && balanceCovers(wallet.balance, amountDue);
  const walletSelectable = allowWallet && !isImpersonating && walletCovers;
  const usableCard = defaultPaymentMethod !== null && isUsableCard(defaultPaymentMethod) ? defaultPaymentMethod : null;
  const cardSelectable = !isImpersonating && usableCard !== null;

  // Snap an impossible selection back to checkout (e.g. the wallet finished loading and is short).
  React.useEffect(() => {
    if (walletLoading) {
      return;
    }
    if ((value === 'wallet' && !walletSelectable) || (value === 'card' && !cardSelectable)) {
      onChange('checkout');
    }
  }, [value, walletSelectable, cardSelectable, walletLoading, onChange]);

  const name = `${idPrefix}-payment-source`;

  const walletDescription = (() => {
    if (!allowWallet) {
      return null;
    }
    if (isImpersonating) {
      return 'Not available during an impersonation session.';
    }
    if (walletLoading) {
      return (
        <>
          <Spinner size="sm" aria-label="Loading your prepaid balance" /> Loading your balance…
        </>
      );
    }
    if (walletError || wallet === null) {
      return 'Your prepaid balance could not be loaded.';
    }
    if (amountDue === null) {
      return `Balance: ${formatPrice(wallet.balance)}. The amount to pay isn't known yet.`;
    }
    return walletCovers
      ? `Balance: ${formatPrice(wallet.balance)} — the full amount is debited now.`
      : `Balance: ${formatPrice(wallet.balance)} — insufficient for ${formatPrice(amountDue)}. Top up your balance or pay by card.`;
  })();

  const cardDescription = (() => {
    if (isImpersonating) {
      return 'Not available during an impersonation session.';
    }
    if (walletLoading) {
      return 'Checking for a saved card…';
    }
    if (usableCard) {
      return `${describeCard(usableCard)} · expires ${formatCardExpiry(usableCard)} — charged in the background, no redirect.`;
    }
    if (defaultPaymentMethod && defaultPaymentMethod.is_expired) {
      return `${describeCard(defaultPaymentMethod)} has expired. Add a new card from Organization › Billing.`;
    }
    if (defaultPaymentMethod && defaultPaymentMethod.status !== 'valid') {
      return `${describeCard(defaultPaymentMethod)} is no longer valid. Add a new card from Organization › Billing.`;
    }
    return 'No saved card on file. Save one by ticking "Save this card" on a checkout, or from Organization › Billing.';
  })();

  return (
    <div role="radiogroup" aria-label="Payment source">
      {allowWallet && (
        <Radio
          id={`${idPrefix}-source-wallet`}
          name={name}
          label="Prepaid balance"
          description={walletDescription}
          isChecked={value === 'wallet'}
          onChange={() => onChange('wallet')}
          isDisabled={isDisabled || !walletSelectable}
        />
      )}
      <Radio
        id={`${idPrefix}-source-card`}
        name={name}
        label="Saved card"
        description={cardDescription}
        isChecked={value === 'card'}
        onChange={() => onChange('card')}
        isDisabled={isDisabled || !cardSelectable}
        className="pf-v6-u-mt-sm"
      />
      <Radio
        id={`${idPrefix}-source-checkout`}
        name={name}
        label="Card or another method at checkout"
        description="You will be redirected to our payment provider to pay securely."
        isChecked={value === 'checkout'}
        onChange={() => onChange('checkout')}
        isDisabled={isDisabled}
        className="pf-v6-u-mt-sm"
        body={
          value === 'checkout' && !isImpersonating ? (
            <Checkbox
              id={`${idPrefix}-save-card`}
              label="Save this card for later one-click payments"
              description="The checkout is then card-only. Only the card's brand, last four digits, holder and expiry are kept — never the card number."
              isChecked={saveCard}
              onChange={(_event, checked) => onSaveCardChange(checked)}
              isDisabled={isDisabled}
            />
          ) : undefined
        }
      />
      {isImpersonating && (
        <Alert variant="info" isInline isPlain title="Impersonation session" className="pf-v6-u-mt-sm">
          <Content component="p">
            Only a hosted checkout can be started for this organization: the card holder pays in the browser, and the
            card is not saved.
          </Content>
        </Alert>
      )}
    </div>
  );
};

export default PaymentSourceChooser;
